<template>
  <div class="modern-header" :style="{ backgroundColor: white }">
    <div class="header-container">
      <!-- Left Section - Logo & Title -->
      <div class="header-left">
        <AppLink strclass="navbar-brand" name="global-feed"></AppLink>
        <div class="title-section">
          <h3 class="header-title">
            <span
              :style="{ color: titleColor }"
              @click="navigateToHome"
              class="title-primary"
            >
              {{ title }}
            </span>
            <span class="title-secondary">{{ updatedTitle }}</span>
          </h3>
        </div>
      </div>

      <!-- Right Section - Actions -->
      <div v-if="$route.name !== pageView.LOGINVIEW" class="header-right">
        <!-- Dev Mode UI Toggle -->
        <div class="ui-toggle-wrapper" v-if="isDev" @click="toggleUI">
          <div class="ui-toggle" :class="{ active: uiToggle.showNewUI }">
            <span class="ui-toggle-label">Switch to Classic UI</span>

            <div class="ui-toggle-track">
              <div class="ui-toggle-handle"></div>
            </div>
          </div>
        </div>

        <!-- Theme Toggle -->
        <!-- Theme Toggle with Icon Only -->
        <div class="theme-dropdown" @click.stop>
          <button
            class="header-icon-btn theme-toggle"
            @click="isThemeMenuOpen = !isThemeMenuOpen"
            title="Change Theme"
            aria-label="Change Theme"
          >
            <!-- Dynamic SVG Icon based on current theme -->
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <transition name="fade" mode="out-in">
                <g v-if="currentTheme === THEMES.AUTO" key="auto">
                  <circle cx="12" cy="12" r="5" />
                  <path
                    d="M12 1v2 M12 21v2 M4.22 4.22l1.42 1.42 M18.36 18.36l1.42 1.42 M1 12h2 M21 12h2 M4.22 19.78l1.42-1.42 M18.36 5.64l1.42-1.42"
                  />
                </g>
                <g
                  v-else-if="
                    currentTheme === THEMES.CLASSIC_LIGHT ||
                    currentTheme === THEMES.PURE_LIGHT ||
                    currentTheme === THEMES.DESERT_SAND
                  "
                  key="light"
                >
                  <circle cx="12" cy="12" r="5" />
                  <path
                    d="M12 1v2 M12 21v2 M4.22 4.22l1.42 1.42 M18.36 18.36l1.42 1.42 M1 12h2 M21 12h2 M4.22 19.78l1.42-1.42 M18.36 5.64l1.42-1.42"
                  />
                </g>
                <g
                  v-else-if="
                    currentTheme === THEMES.MODERN_DARK ||
                    currentTheme === THEMES.DEEP_BLACK ||
                    currentTheme === THEMES.CYBER_NEON
                  "
                  key="dark"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </g>
              </transition>
            </svg>
          </button>

          <!-- Theme Dropdown Menu (unchanged, still shows labels) -->
          <div
            v-if="isThemeMenuOpen"
            class="theme-dropdown-menu"
            ref="themeMenu"
          >
            <div
              v-for="item in themes"
              :key="item.value"
              class="theme-item"
              :class="{ active: currentTheme === item.value }"
              @click="applyTheme(item.value) ; isThemeMenuOpen = !isThemeMenuOpen"
            >
              {{ item.label }}
            </div>
          </div>
        </div>

        <!-- Help Button -->
        <button
          class="header-icon-btn"
          @click="redirectToPaveDoc"
          v-tooltip="supportToolTip"
          aria-label="Help & Documentation"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M10 14V14.01M10 11C10 10.5 10.5 10 11 9.5C11.5 9 12 8.5 12 7.5C12 6.67157 11.3284 6 10.5 6C9.67157 6 9 6.67157 9 7.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <!-- Profile Dropdown -->
        <div v-if="!hideProfileIcon" class="profile-dropdown">
          <button
            class="profile-button"
            type="button"
            id="dropdownMenuButton"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <div class="profile-avatar">
              <span class="avatar-letter">
                {{ userName ? userName.charAt(0).toUpperCase() : "?" }}
              </span>
            </div>

            <span class="profile-name">{{ userName }}</span>
            <svg
              class="dropdown-arrow"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <ul
            class="modern-dropdown-menu dropdown-menu dropdown-menu-end"
            aria-labelledby="dropdownMenuButton"
          >
            <!-- User Info Header -->
            <li class="dropdown-header">
              <div class="user-info-header">
                <div class="user-avatar-large">
                  <span class="avatar-letter">
                    {{ userName ? userName.charAt(0).toUpperCase() : "?" }}
                  </span>
                </div>

                <div class="user-details">
                  <div class="user-name">{{ userName }}</div>
                  <div class="user-email">{{ role }}</div>
                </div>
              </div>
            </li>

            <li><hr class="dropdown-divider" /></li>

            <!-- User Management (Admin Only) -->
            <li v-if="isUserSuperAdmin">
              <button
                @click="manageRoles"
                class="modern-dropdown-item"
                type="button"
              >
                <svg
                  class="item-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M11 14V12.6667C11 11.9594 10.719 11.2811 10.219 10.781C9.71895 10.281 9.04058 10 8.33333 10H3.66667C2.95942 10 2.28105 10.281 1.78105 10.781C1.28095 11.2811 1 11.9594 1 12.6667V14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6 7.33333C7.47276 7.33333 8.66667 6.13943 8.66667 4.66667C8.66667 3.19391 7.47276 2 6 2C4.52724 2 3.33333 3.19391 3.33333 4.66667C3.33333 6.13943 4.52724 7.33333 6 7.33333Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M15 14V12.6667C14.9997 12.0758 14.8044 11.5019 14.4471 11.0349C14.0898 10.5679 13.5909 10.2344 13.0267 10.0867"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.6934 2.08667C11.2592 2.23354 11.7596 2.56714 12.1179 3.03488C12.4762 3.50262 12.672 4.07789 12.672 4.67C12.672 5.26211 12.4762 5.83738 12.1179 6.30512C11.7596 6.77286 11.2592 7.10646 10.6934 7.25333"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>User Management</span>
              </button>
            </li>

            <!-- Downloads -->
            <li>
              <button
                @click="manageDownloads"
                class="modern-dropdown-item"
                type="button"
              >
                <svg
                  class="item-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.66699 6.66667L8.00033 10L11.3337 6.66667"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 10V2"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Downloads</span>
              </button>
            </li>

            <!-- Trash -->
            <li>
              <button
                type="button"
                class="modern-dropdown-item"
                @click="trashView(projectType, userId)"
              >
                <svg
                  class="item-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M2 4H3.33333H14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M5.33301 4.00016V2.66683C5.33301 2.31321 5.47348 1.97407 5.72353 1.72402C5.97358 1.47397 6.31272 1.3335 6.66634 1.3335H9.33301C9.68663 1.3335 10.0258 1.47397 10.2758 1.72402C10.5259 1.97407 10.6663 2.31321 10.6663 2.66683V4.00016M12.6663 4.00016V13.3335C12.6663 13.6871 12.5259 14.0263 12.2758 14.2763C12.0258 14.5264 11.6866 14.6668 11.333 14.6668H4.66634C4.31272 14.6668 3.97358 14.5264 3.72353 14.2763C3.47348 14.0263 3.33301 13.6871 3.33301 13.3335V4.00016H12.6663Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Trash</span>
              </button>
            </li>

            <li><hr class="dropdown-divider" /></li>

            <!-- Logout -->
            <li>
              <button
                @click="logout"
                class="modern-dropdown-item logout-item"
                type="button"
              >
                <svg
                  class="item-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.667 11.3333L14.0003 8L10.667 4.66667"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M14 8H6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Log Off</span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCookie, deleteCookie } from "@/store/cookie";
import { user, pageViews, role, type } from "@/utils/util";
import { makeAuthenticationRequest, getTitle } from "@/services/apiServices";
import { useAuthStore } from "../../store/auth.js";
import { redirect } from "@/utils/routerUtils.js";
import { getSessionItem, setSessionItem } from "@/store/session.js";
import ReportTemplate from "@/components/ReportTemplate.vue";
import { useSortStore, useTableColumnStore } from "../../store/sort.js";
import { toast } from "vue3-toastify";
// import { themeManager } from "@/composables/useTheme.js";
import { useTheme, THEMES } from "@/composables/useTheme.js";
import { useUiToggleStore } from "@/store/uiToggleStore";
import { watchEffect, onMounted } from "vue";
export default {
  name: pageViews.HEADERVIEW,
  components: { ReportTemplate },
  data() {
    return {
      userId: getCookie(user.id),
      projectType: type.PROJECT,
      userName: getCookie(user.name),
      role: getCookie(user.role),
      updatedTitle: "",
      isToggled: false,
      newView: "Try New View",
      trashViewToolTip: "Trash",
      supportToolTip: "User Guide",
      pageView: pageViews,
      isThemeMenuOpen: false,
      themes: [
        { value: THEMES.AUTO, label: "Auto" },
        { value: THEMES.CLASSIC_LIGHT, label: "Classic Light" },
        { value: THEMES.PURE_LIGHT, label: "Pure Light" },
        { value: THEMES.DESERT_SAND, label: "Desert Sand" },
        { value: THEMES.MODERN_DARK, label: "Modern Dark" },
        { value: THEMES.CYBER_NEON, label: "Cyber Neon" },
        { value: THEMES.DEEP_BLACK, label: "Deep Black" },
      ],
    };
  },

  mounted() {
    // Safe click outside handler
    this.clickOutsideHandler = (e) => {
      if (!this.$el.querySelector(".theme-dropdown")?.contains(e.target)) {
        this.isThemeMenuOpen = false;
      }
    };
    document.addEventListener("click", this.clickOutsideHandler);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.clickOutsideHandler);
  },

  computed: {
    hideProfileIcon() {
      return this.$route.meta.hideProfileIcon;
    },
    titleColor() {
      return this.$route.meta.titleColor;
    },
    bgColor() {
      return this.$route.meta.bgColor;
    },
    title() {
      return this.$route.meta.title;
    },
    isUserSuperAdmin() {
      return getCookie(user.role) === role.superadmin;
    },
    isNewViewRolledOut() {
      return this.$acclConfigs["rollout-new-view"];
    },
    computed: {
      themeTooltip() {
        switch (this.theme) {
          case "light":
            return "Switch to Dark Mode";
          case "dark":
            return "Switch to Neon Mode";
          case "neon":
            return "Switch to Full Dark Mode";
          default:
            return "Switch to Light Mode";
        }
      },
    },
  },

  watch: {
    "$route.params.projectParamId": function (projectId) {
      this.updateTitle(projectId, this.$route.name);
    },
  },
  updated() {
    this.userName = getCookie(user.name);
  },
  routerParams() {
    return this.$route.params;
  },
  methods: {
    toggleUI() {
      const newValue = !this.uiToggle.showNewUI;
      this.uiToggle.setShowNewUI(newValue);
      window.location.reload();
      console.log(newValue ? "Switched to New UI" : "Switched to Classic UI");
    },

    redirectToPaveDoc() {
      window.open(
        window.acclConfigs.VUE_APP_SUPPORT_URL,
        "_blank" // <- This is what makes it open in a new window.
      );
      //window.location.href = window.acclConfigs.VUE_APP_SUPPORT_URL;
    },
    setValuesToStore(componentType, userId, requestId, convertedFileName) {
      setSessionItem("userId", userId);
      setSessionItem("componentType", componentType);
    },
    async trashView(type, userId) {
      console.log("Type:", type);
      this.setValuesToStore(type, userId);
      this.redirectView(pageViews.TRASHVIEW, {});
    },
    async redirectView(viewName, txnData) {
      redirect(
        type.PROJECT,
        this.$router,
        this.routerParams,
        viewName,
        txnData
      );
    },
    async logout() {
      const body = {
        userId: getCookie(user.id),
        authSource: getCookie(user.source),
      };
      let toastId = toast.loading("You are logging out", {
        autoClose: false,
        closeOnClick: false,
      });
      try {
        const response = await makeAuthenticationRequest(
          this.$acclConfigs,
          "/logout",
          body
        );
        if (response.status == 200) {
          // toast.success('You are logged out', {
          //     autoClose: 3000,
          // });

          toast.update(toastId, {
            render: "You have been logged out!",
            type: "success", // Change the toast type
            isLoading: false,
            autoClose: 2000, // Set a timeout for the updated toast
          });
          deleteCookie(user.id, user.name, user.source);
          this.clearJWTToken();
          this.sortStore.clearSortStates();
          this.tableStore.clearTableStates();
          this.$router.push({
            name: pageViews.LOGINVIEW,
          });
        }
      } catch (error) {
        console.log("Error message: Logout API Failed: ", error.message);
      }
    },
    toggle() {
      this.isToggled = !this.isToggled;
      if (this.isToggled) {
        this.newView = "Switch to Classic View";
        //window.location.href = 'https://google.com';
        this.$router.push({ name: pageViews.VIEW_404 });
      } else {
        this.newView = "Try New View";
        this.$router.push({ name: pageViews.ALLINONE_ACC_VIEW });
      }
    },
    navigateToHome() {
      if (this.isToggled) {
        this.isToggled = !this.isToggled;
        this.newView = "Try New View";
      }
      console.log(this.isToggled);
      this.$router.push({
        name: pageViews.ALLINONE_ACC_VIEW,
      });
    },
    async updateTitle(projectId, routeName) {
      if (
        ![
          pageViews.ALLINONE_ACC_VIEW,
          pageViews.DOWNLOADS_VIEW,
          pageViews.TRASHVIEW,
          pageViews.LOGINVIEW,
          pageViews.VIEW_404,
          pageViews.ERROR_VIEW,
          pageViews.MANAGE_USERS_VIEW,
        ].includes(routeName)
      ) {
        try {
          const response = await getTitle(
            this.$acclConfigs,
            projectId,
            this.userId,
            "project"
          );
          if (response.status == 200) {
            let prefix = "-";
            this.updatedTitle = prefix + " " + response.data.title;
          }
        } catch (error) {
          console.error("Error making api call - getTitle()", error);
        }
      } else {
        this.updatedTitle = "";
      }
    },
    async manageRoles() {
      this.$router.push({
        name: pageViews.MANAGE_USERS_VIEW,
      });
    },
    async manageDownloads() {
      this.$router.push({
        name: pageViews.DOWNLOADS_VIEW,
      });
    },
    setValuesToStore(componentType, userId) {
      setSessionItem("userId", userId);
      setSessionItem("componentType", componentType);
    },
  },
  setup() {
    const uiToggle = useUiToggleStore();

    // other setup stores you already have
    const sortStore = useSortStore();
    const authStore = useAuthStore();
    const tableStore = useTableColumnStore();

    const environment = window?.acclConfigs?.VUE_APP_ENVIRONMENT || "prod"; // default fallback

    const isDev = environment === "dev";
    const { currentTheme, displayName, applyTheme } = useTheme();

    onMounted(() => {
      // Apply background based on initial toggle state
      document.body.classList.toggle("old-ui-bg", !uiToggle.showNewUI);
    });

    watchEffect(() => {
      if (uiToggle.showNewUI) {
        document.body.classList.remove("old-ui-bg");
      } else {
        document.body.classList.add("old-ui-bg");
      }
    });

    function clearJWTToken() {
      authStore.clearToken();
    }
    return {
      clearJWTToken,
      sortStore,
      tableStore,
      uiToggle,
      isDev,
      currentTheme,
      displayName,
      applyTheme,
      THEMES,
    };
  },
};
</script>

<style scoped>
.modern-header {
  background: var(--surface);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 1px 2px var(--shadow-soft);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  max-width: 100%;
  margin: 0 auto;
}

/* === LEFT SECTION === */
.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.title-section {
  display: flex;
  align-items: center;
}

.header-title {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
}

.title-primary {
  color: var(--text-primary) !important;
  cursor: pointer;
  position: relative;
  transition: color 0.25s ease;
}

.title-primary:hover {
  color: var(--primary);
}

.title-primary::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--primary);
  transition: width 0.3s ease;
}

.title-primary:hover::after {
  width: 100%;
}

.title-secondary {
  color: var(--text-secondary);
  font-weight: 400;
  font-size: 20px;
}

/* === RIGHT SECTION === */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* === ICON BUTTONS === */
.header-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.header-icon-btn:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
  border-color: var(--text-secondary);
}

.header-icon-btn:active {
  transform: scale(0.95);
}

.ui-toggle-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
}

/* Toggle container */
.ui-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface-highlight);
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  transition: background 0.25s ease, border-color 0.25s ease;
  user-select: none;
}

/* Active look */
.ui-toggle.active {
  background: var(--primary-light);
  border-color: var(--primary);
}

/* Label */
.ui-toggle-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.25s ease;
}

/* Toggle track */
.ui-toggle-track {
  width: 44px;
  height: 22px;
  background: var(--border-color);
  border-radius: 20px;
  position: relative;
  transition: background 0.25s ease;
}

/* Active track */
.ui-toggle.active .ui-toggle-track {
  background: #3b3b3b;
}

/* Handle */
.ui-toggle-handle {
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 3px;
  transition: transform 0.25s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
}

/* Slide right when active */
.ui-toggle.active .ui-toggle-handle {
  transform: translateX(0);
}

.slider {
  width: 18px;
  height: 18px;
  background: var(--surface);
  border-radius: 50%;
  position: absolute;
  left: 4px;
  top: 3px;
  transition: transform 0.25s ease;
}

.ui-toggle.active .slider {
  transform: translateX(24px);
}

/* === NOTIFICATION BADGE === */
.notification-btn {
  position: relative;
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--error);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid var(--surface);
}

/* === PROFILE BUTTON === */
.profile-button {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 4px 12px 4px 4px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.25s ease;
}

.profile-button:hover {
  background: var(--surface-hover);
  border-color: var(--text-secondary);
}

.profile-avatar {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--primary, #696cff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
}

.profile-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-arrow {
  color: var(--text-secondary);
  transition: transform 0.25s ease;
}

.theme-dropdown {
  position: relative;
}

.theme-dropdown-menu {
  position: absolute;
  right: 0;
  top: 40px;
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 6px 0;
  width: 150px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.theme-item {
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  text-transform: capitalize;
  color: var(--text-primary);
}

.theme-item:hover {
  background: var(--surface-hover);
}

.theme-item.active {
  font-weight: 600;
  background-color: var(--primary-hover);
}

.profile-button[aria-expanded="true"] .dropdown-arrow {
  transform: rotate(180deg);
}

/* === DROPDOWN MENU === */
.modern-dropdown-menu {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--surface);
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.03),
    0 12px 16px -4px rgba(16, 24, 40, 0.08);
  padding: 8px;
  min-width: 240px;
  margin-top: 8px;
  animation: slideDown 0.2s ease-out;
}

/* === DROPDOWN HEADER === */
.dropdown-header {
  padding: 12px;
  margin-bottom: 4px;
}

.user-info-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar-large {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: var(--primary, #696cff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  font-size: 20px;
  text-transform: uppercase;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-email {
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* === DROPDOWN ITEMS === */
.modern-dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modern-dropdown-item:hover {
  background: var(--surface-hover);
}

.modern-dropdown-item:active {
  background: var(--surface-active);
}

.item-icon {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.modern-dropdown-item:hover .item-icon {
  color: var(--text-primary);
}

/* === LOGOUT ITEM === */
.logout-item {
  color: var(--error);
}

.logout-item .item-icon {
  color: var(--error);
}

.logout-item:hover {
  background: var(--error-bg, var(--surface-hover));
  color: var(--error);
}

/* === DROPDOWN DIVIDER === */
.modern-dropdown-menu .dropdown-divider {
  margin: 8px 0;
  border-color: var(--border-color);
}

/* === RESPONSIVE === */
@media (max-width: 768px) {
  .header-container {
    padding: 12px 16px;
  }

  .header-title {
    font-size: 20px;
  }

  .title-secondary {
    font-size: 16px;
  }

  .profile-name {
    display: none;
  }

  .profile-button {
    padding: 4px;
    width: 40px;
    justify-content: center;
  }

  .notification-btn {
    display: none;
  }
}

@media (max-width: 480px) {
  .header-title {
    font-size: 18px;
  }

  .title-secondary {
    display: none;
  }
}

/* === ANIMATIONS === */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === FOCUS STATES === */
.header-icon-btn:focus-visible,
.profile-button:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.modern-dropdown-item:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

/* === DEV MODE TOGGLE === */
.floating-toggle-container {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 6px 10px;
  height: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.floating-toggle-container:hover {
  background: var(--surface-hover);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.toggle-label input[type="checkbox"] {
  appearance: none;
  width: 32px;
  height: 18px;
  border-radius: 9px;
  background: var(--border-color);
  position: relative;
  transition: background 0.3s ease;
  cursor: pointer;
}

.toggle-label input[type="checkbox"]:checked {
  background: var(--primary);
}

.toggle-label input[type="checkbox"]::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.3s ease;
}

.toggle-label input[type="checkbox"]:checked::before {
  transform: translateX(14px);
}

.toggle-text {
  color: var(--text-secondary);
  font-size: 13px;
  user-select: none;
}
</style>
